import { defineStore } from "pinia";

// NOTE: do NOT add `persist: true` (pinia-plugin-persistedstate) — this store
// owns its own cookie + localStorage envelope below. Adding the plugin will
// cause conflicting writes.

export type ConsentDecision = "pending" | "accepted" | "rejected" | "custom";

export interface ConsentCategories {
	necessary: true;
	statistics: boolean;
	marketing: boolean;
	preferences: boolean;
}

export interface GoogleConsentPayload {
	ad_storage: "granted" | "denied";
	ad_user_data: "granted" | "denied";
	ad_personalization: "granted" | "denied";
	analytics_storage: "granted" | "denied";
	personalization_storage: "granted" | "denied";
	functionality_storage: "granted" | "denied";
	security_storage: "granted";
}

interface State {
	decision: ConsentDecision;
	categories: ConsentCategories;
	updatedAt: number;
	settingsOpen: boolean;
}

interface StoredEnvelope {
	v: 1;
	decision: ConsentDecision;
	categories: ConsentCategories;
	updatedAt: number;
}

const COOKIE_NAME = "cookie_consent";
const COOKIE_MAX_AGE = 60 * 60 * 24 * 180; // 180 days
const STORAGE_VERSION = 1 as const;

const defaultCategories = (): ConsentCategories => ({
	necessary: true,
	statistics: false,
	marketing: false,
	preferences: false
});

const dispatchGoogleConsentUpdate = (payload: GoogleConsentPayload) => {
	if (typeof window === "undefined") return;
	const w = window as unknown as { dataLayer?: unknown[] };
	w.dataLayer = w.dataLayer || [];
	w.dataLayer.push(["consent", "update", payload]);
};

const parseEnvelope = (raw: unknown): StoredEnvelope | null => {
	if (!raw || typeof raw !== "object") return null;
	const env = raw as Partial<StoredEnvelope>;
	if (env.v !== STORAGE_VERSION) return null;
	if (!env.decision || !env.categories) return null;
	return env as StoredEnvelope;
};

export const useCookieConsentStore = defineStore("cookieConsentStore", {
	state: (): State => ({
		decision: "pending",
		categories: defaultCategories(),
		updatedAt: 0,
		settingsOpen: false
	}),

	getters: {
		hasDecided: (s) => s.decision !== "pending",
		googleConsentPayload: (s): GoogleConsentPayload => ({
			ad_storage: s.categories.marketing ? "granted" : "denied",
			ad_user_data: s.categories.marketing ? "granted" : "denied",
			ad_personalization: s.categories.marketing ? "granted" : "denied",
			analytics_storage: s.categories.statistics ? "granted" : "denied",
			personalization_storage: s.categories.preferences ? "granted" : "denied",
			functionality_storage: s.categories.preferences ? "granted" : "denied",
			security_storage: "granted"
		})
	},

	actions: {
		hydrateFromCookie() {
			const cookieRef = useCookie<StoredEnvelope | null>(COOKIE_NAME, {
				maxAge: COOKIE_MAX_AGE,
				sameSite: "lax",
				default: () => null
			});
			const env = parseEnvelope(cookieRef.value);
			if (env) {
				this.decision = env.decision;
				this.categories = {
					...defaultCategories(),
					...env.categories,
					necessary: true
				};
				this.updatedAt = env.updatedAt;
			}
		},

		persist() {
			if (import.meta.server) return;
			const envelope: StoredEnvelope = {
				v: STORAGE_VERSION,
				decision: this.decision,
				categories: this.categories,
				updatedAt: this.updatedAt
			};
			const cookieRef = useCookie<StoredEnvelope | null>(COOKIE_NAME, {
				maxAge: COOKIE_MAX_AGE,
				sameSite: "lax",
				default: () => null
			});
			cookieRef.value = envelope;
		},

		acceptAll() {
			this.categories = {
				necessary: true,
				statistics: true,
				marketing: true,
				preferences: true
			};
			this.decision = "accepted";
			this.updatedAt = Date.now();
			this.persist();
			dispatchGoogleConsentUpdate(this.googleConsentPayload);
			this.settingsOpen = false;
		},

		rejectAll() {
			this.categories = defaultCategories();
			this.decision = "rejected";
			this.updatedAt = Date.now();
			this.persist();
			dispatchGoogleConsentUpdate(this.googleConsentPayload);
			this.settingsOpen = false;
		},

		savePreferences(partial: Partial<Omit<ConsentCategories, "necessary">>) {
			this.categories = {
				...this.categories,
				...partial,
				necessary: true
			};
			this.decision = "custom";
			this.updatedAt = Date.now();
			this.persist();
			dispatchGoogleConsentUpdate(this.googleConsentPayload);
			this.settingsOpen = false;
		},

		// Mirror an external CMP's decision (e.g. Cookiebot on the breadandboxers
		// template) into this store so `useCookieConsent()` stays the single
		// source of truth across all templates. Unlike acceptAll/savePreferences
		// this intentionally does NOT persist() — the external CMP owns its own
		// consent cookie — and does NOT dispatch a Google Consent Mode update,
		// since Cookiebot's `data-framework: "gtm"` already drives Consent Mode
		// (re-dispatching here would double-signal).
		syncFromCmp(categories: Partial<Omit<ConsentCategories, "necessary">>) {
			this.categories = {
				...defaultCategories(),
				...categories,
				necessary: true
			};
			const granted = [
				this.categories.statistics,
				this.categories.marketing,
				this.categories.preferences
			];
			this.decision = granted.every(Boolean)
				? "accepted"
				: granted.some(Boolean)
					? "custom"
					: "rejected";
			this.updatedAt = Date.now();
		},

		revoke() {
			this.decision = "pending";
			this.categories = defaultCategories();
			this.updatedAt = 0;
			if (import.meta.client) {
				const cookieRef = useCookie<StoredEnvelope | null>(COOKIE_NAME, {
					maxAge: COOKIE_MAX_AGE,
					sameSite: "lax",
					default: () => null
				});
				cookieRef.value = null;
			}
			dispatchGoogleConsentUpdate(this.googleConsentPayload);
		},

		openSettings() {
			this.settingsOpen = true;
		},

		closeSettings() {
			this.settingsOpen = false;
		}
	}
});
